import AddIcon from '@mui/icons-material/Add'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import KuBadge from '../../components/common/KuBadge'
import Stars from '../../components/common/Stars'
import { KnowledgeUnit, PassMeta, Prompt, Provider, PROVIDERS } from '../../types'

interface Props {
  pass: PassMeta
  standardId: string
  standardText: string
  subject: string
  grade: string | number
  kus: KnowledgeUnit[]
  extractPrompt: Prompt | undefined
  generatePrompt: Prompt | undefined
  canGenerate: boolean
  onGenerate: (provider: Provider, model: string) => Promise<void>
  onOpenKU: (ku: KnowledgeUnit) => void
  userId: string
  onVote: (kuId: string, score: number) => Promise<void>
}

export default function PassCard({ pass, kus, canGenerate, onGenerate, onOpenKU, userId, onVote }: Props) {
  const [expanded, setExpanded] = useState(true)
  const [provider, setProvider] = useState<Provider>('anthropic')
  const [model, setModel] = useState(PROVIDERS[0].models[0].id)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const acceptedCount = kus.filter((k) => k.status === 'accepted').length
  const draftCount = kus.filter((k) => k.status === 'draft').length

  const handleProviderChange = (p: Provider) => {
    setProvider(p)
    const provMeta = PROVIDERS.find((pr) => pr.id === p)
    if (provMeta) setModel(provMeta.models[0].id)
  }

  const handleGenerate = async () => {
    setGenerating(true)
    setError(null)
    try {
      await onGenerate(provider, model)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  const models = PROVIDERS.find((p) => p.id === provider)?.models || []

  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: pass.color + '40', overflow: 'visible' }}>
      {/* Pass header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: pass.bg,
          cursor: 'pointer',
          borderRadius: '10px 10px 0 0',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: pass.color + '30',
        }}
      >
        <Typography sx={{ fontSize: 18 }}>{pass.icon}</Typography>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: pass.color,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontSize: 10,
            }}
          >
            {pass.label}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 11, lineHeight: 1.3 }}>
            {pass.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {acceptedCount > 0 && (
            <Chip
              label={`${acceptedCount} ✓`}
              size="small"
              sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700, height: 20, fontSize: 10 }}
            />
          )}
          {draftCount > 0 && (
            <Chip
              label={`${draftCount} draft`}
              size="small"
              sx={{ bgcolor: '#f3f4f6', color: '#6b7280', fontWeight: 700, height: 20, fontSize: 10 }}
            />
          )}
        </Box>
      </Box>

      <Collapse in={expanded}>
        {/* Pass description */}
        <Box sx={{ px: 2, py: 1, bgcolor: pass.bg + '66' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            {pass.desc}
          </Typography>
        </Box>

        {/* Generate controls — researcher/admin only */}
        {canGenerate && (
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: 12 }}>Provider</InputLabel>
                <Select
                  value={provider}
                  label="Provider"
                  onChange={(e) => handleProviderChange(e.target.value as Provider)}
                  sx={{ fontSize: 12 }}
                >
                  {PROVIDERS.map((p) => (
                    <MenuItem key={p.id} value={p.id} sx={{ fontSize: 12 }}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: 12 }}>Model</InputLabel>
                <Select value={model} label="Model" onChange={(e) => setModel(e.target.value)} sx={{ fontSize: 12 }}>
                  {models.map((m) => (
                    <MenuItem key={m.id} value={m.id} sx={{ fontSize: 12 }}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                startIcon={kus.length > 0 ? <AutorenewIcon /> : <AddIcon />}
                onClick={handleGenerate}
                disabled={generating}
                sx={{ bgcolor: pass.color, '&:hover': { bgcolor: pass.color + 'cc' }, fontSize: 12, py: 0.75 }}
              >
                {generating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : kus.length > 0 ? (
                  'Regenerate'
                ) : (
                  'Generate'
                )}
              </Button>
            </Box>
            {error && (
              <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* KU list */}
        {kus.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              {canGenerate ? 'No KUs yet — generate to begin.' : 'No KUs extracted yet.'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {kus.map((ku, idx) => {
              const myVote = ku.votes?.find((v) => v.user_id === userId)
              return (
                <Box key={ku.id}>
                  <Box
                    onClick={() => onOpenKU(ku)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.50' },
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', fontWeight: 700, minWidth: 18, mt: 0.25 }}
                    >
                      {idx + 1}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: 'text.primary' }}>
                          {ku.name}
                        </Typography>
                        <KuBadge type={ku.proposed_type} />
                        {ku.status === 'accepted' && (
                          <Chip
                            label="✓"
                            size="small"
                            sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700, height: 18, fontSize: 10 }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: 12, display: 'block', lineHeight: 1.5 }}
                      >
                        {ku.description}
                      </Typography>
                    </Box>
                    <Stars value={myVote?.score || 0} size={14} />
                  </Box>
                  {idx < kus.length - 1 && <Divider />}
                </Box>
              )
            })}
          </Box>
        )}
      </Collapse>
    </Card>
  )
}
